import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  MoneyField,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';

import { reconcileCashDrawerAtom } from '../../_graphql/mutationReconcileCashDrawer';

import { useTranslations } from '@/finance/i18n/useTranslations';

interface ReconcileCashDrawerDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  financialAccountId: string;
  currency: string;
  onSuccess?: () => void;
}

export const ReconcileCashDrawerDialog: React.FC<
  ReconcileCashDrawerDialogProps
> = ({ isOpen, onOpenChange, financialAccountId, currency, onSuccess }) => {
  const t = useTranslations();
  const toast = useToast();
  const reconcile = useSetAtom(reconcileCashDrawerAtom());

  const [physicalCount, setPhysicalCount] = React.useState<number>(0);
  const [notes, setNotes] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleClose = React.useCallback(() => {
    onOpenChange(false);
    setPhysicalCount(0);
    setNotes('');
  }, [onOpenChange]);

  const handleSubmit = React.useCallback(async () => {
    setIsSubmitting(true);
    try {
      await reconcile({
        financialAccountId,
        physicalCount,
        ...(notes ? { notes } : {}),
      });
      toast.success(t.financialAccounts.reconcile.messages.success);
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(
        t.financialAccounts.reconcile.messages.error(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    reconcile,
    financialAccountId,
    physicalCount,
    notes,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={() => handleClose()}
      className="@ReconcileCashDrawerDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle>{t.financialAccounts.reconcile.title}</DialogTitle>
        </DialogHeader>
        <DialogMain>
          <div className="flex flex-col gap-4">
            <MoneyField
              label={t.financialAccounts.reconcile.fields.physicalCount}
              amount={physicalCount}
              onAmountChange={setPhysicalCount}
              currencyCode={currency}
              currencyLocked
              isRequired
            />
            <TextField
              label={t.financialAccounts.reconcile.fields.notes}
              value={notes}
              onChange={setNotes}
            />
          </div>
        </DialogMain>
        <DialogFooter>
          <div className="flex justify-end gap-2">
            <Button variant="text" color="neutral" onPress={handleClose}>
              {t.financialAccounts.reconcile.buttons.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSubmit}
              isPending={isSubmitting}
            >
              {t.financialAccounts.reconcile.buttons.reconcile}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
